import { useEffect, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { toast } from 'sonner';
import type { Alert } from '@/api/services/alerts.api';

/**
 * Custom Hook for Real-Time Alert Notifications
 *
 * This hook establishes a WebSocket connection to receive real-time updates
 * for new alerts, alert updates, and alert dismissals.
 *
 * Phase 1 - Real-Time Alerts
 */

export interface NewAlertEvent {
  alert: Alert;
  timestamp: string;
}

export interface AlertUpdatedEvent {
  alert: Alert;
  timestamp: string;
}

export interface AlertRemovedEvent {
  alertId: string;
  timestamp: string;
}

export function useAlerts(companyId: string | undefined) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [latestAlert, setLatestAlert] = useState<Alert | null>(null);

  /**
   * Initialize WebSocket connection
   */
  useEffect(() => {
    if (!companyId) return;

    // Get API URL from environment or use default
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
    const wsUrl = apiUrl.replace(/^http/, 'ws');

    // Create socket connection to /alerts namespace
    const socketInstance = io(`${wsUrl}/alerts`, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      auth: {
        token: localStorage.getItem('weatherford_access_token'), // JWT token for authentication
      },
    });

    // Connection established
    socketInstance.on('connect', () => {
      console.log('[useAlerts] WebSocket connected:', socketInstance.id);
      setIsConnected(true);

      // Subscribe to company alerts
      socketInstance.emit('subscribe_company', companyId);
    });

    // Connection lost
    socketInstance.on('disconnect', (reason) => {
      console.log('[useAlerts] WebSocket disconnected:', reason);
      setIsConnected(false);

      if (reason === 'io server disconnect') {
        // Server disconnected the socket, need to reconnect manually
        socketInstance.connect();
      }
    });

    // Connection error
    socketInstance.on('connect_error', (error) => {
      console.error('[useAlerts] WebSocket connection error:', error);
      setIsConnected(false);
    });

    // Server messages
    socketInstance.on('connected', (data) => {
      console.log('[useAlerts] Server message:', data.message);
    });

    socketInstance.on('subscribed', (data) => {
      console.log('[useAlerts] Subscribed:', data.message);
    });

    socketInstance.on('error', (data) => {
      console.error('[useAlerts] Server error:', data.message);
      toast.error(data.message);
    });

    // New alert event
    socketInstance.on('new_alert', (data: NewAlertEvent) => {
      console.log('[useAlerts] New alert received:', data);
      setLatestAlert(data.alert);
      setUnreadCount((prev) => prev + 1);

      // Show toast notification for high-priority alerts
      if (data.alert.severity === 'CRITICAL') {
        toast.error(data.alert.title, {
          description: data.alert.message,
          duration: 10000, // 10 seconds for critical alerts
        });
      } else if (data.alert.severity === 'HIGH') {
        toast.error(data.alert.title, {
          description: data.alert.message,
        });
      } else if (data.alert.severity === 'MEDIUM') {
        toast.warning(data.alert.title, {
          description: data.alert.message,
        });
      }
    });

    // Alert updated event (acknowledged or dismissed)
    socketInstance.on('alert_updated', (data: AlertUpdatedEvent) => {
      console.log('[useAlerts] Alert updated:', data);

      // If alert was acknowledged or dismissed, decrement unread count
      if (data.alert.isRead || data.alert.isDismissed) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    });

    // Alert removed event
    socketInstance.on('alert_removed', (data: AlertRemovedEvent) => {
      console.log('[useAlerts] Alert removed:', data.alertId);
      setUnreadCount((prev) => Math.max(0, prev - 1));
    });

    // Broadcast messages
    socketInstance.on('broadcast', (data) => {
      console.log('[useAlerts] Broadcast message:', data.message);
      toast.info(data.message);
    });

    setSocket(socketInstance);

    // Cleanup on unmount
    return () => {
      console.log('[useAlerts] Cleaning up WebSocket connection');
      if (companyId) {
        socketInstance.emit('unsubscribe_company', companyId);
      }
      socketInstance.disconnect();
    };
  }, [companyId]); // Re-run if companyId changes

  /**
   * Manually reconnect to WebSocket
   */
  const reconnect = useCallback(() => {
    if (socket && !isConnected) {
      socket.connect();
    }
  }, [socket, isConnected]);

  /**
   * Get connection status
   */
  const getConnectionStatus = useCallback((): 'connected' | 'disconnected' | 'connecting' => {
    if (!socket) return 'disconnected';
    if (isConnected) return 'connected';
    return 'connecting';
  }, [socket, isConnected]);

  return {
    // Connection status
    isConnected,
    connectionStatus: getConnectionStatus(),

    // Alert state
    unreadCount,
    latestAlert,
    setUnreadCount, // Allow manual updates (e.g., when loading initial count)

    // Actions
    reconnect,

    // Socket instance (for advanced usage)
    socket,
  };
}
